import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, alpha, Box, Button, CircularProgress, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { apiGet } from 'api/service';
import { GREEN, MONO } from './scanUi';
import { whatLabel } from './jobsLogic';
import { PERIODS, chartDays, chartMax, kpis, rateColor } from './analyticsLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const SERIES = [['scans', GREEN, 'Scans'], ['findings', '#60a5fa', 'New findings'], ['cases', '#f59e0b', 'Cases']];
const shortDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    setLoading(true); setError('');
    apiGet(`/api/analytics/overview/?days=${days}`).then((d) => live && setData(d)).catch(() => live && setError('Could not load the analytics.')).finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [days]);

  const cards = useMemo(() => (data ? kpis(data) : []), [data]);
  const bars = useMemo(() => (data ? chartDays(data.daily, days) : []), [data, days]);
  const top = chartMax(bars);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Analytics</Typography>
          <Typography color="text.secondary">How much the platform did, how well it went, and how that compares with the period before</Typography>
        </Box>
        <ToggleButtonGroup exclusive size="small" value={days} onChange={(_, v) => v && setDays(v)}>
          {PERIODS.map((p) => <ToggleButton key={p} value={p} sx={{ textTransform: 'none', px: 2, fontWeight: 600 }}>{p} days</ToggleButton>)}
        </ToggleButtonGroup>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && !data && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: GREEN }} /></Box>}

      {data && (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity .2s' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 2, mb: 2.5 }}>
            {cards.map((k) => (
              <Box key={k.label} sx={{ ...panel, p: 2.2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>{k.label}</Typography>
                <Stack direction="row" alignItems="baseline" gap={1.2} sx={{ mt: 0.5 }}>
                  <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO }}>{k.value}</Typography>
                  {k.delta.good != null && (
                    <Typography sx={{ fontSize: 12, fontWeight: 700, px: 1, py: 0.2, borderRadius: 1.5, bgcolor: alpha(k.delta.good ? GREEN : '#ef4444', 0.16), color: k.delta.good ? '#4ade80' : '#fca5a5' }}>{k.delta.text}</Typography>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">{k.note}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ ...panel, p: 3, mb: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              <Typography fontWeight={600}>Activity {days > 120 ? 'per week' : 'per day'}</Typography>
              <Stack direction="row" gap={2}>{SERIES.map(([, c, l]) => <Typography key={l} variant="caption" color="text.secondary"><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: c, marginRight: 5 }} />{l}</Typography>)}</Stack>
            </Stack>
            <Stack direction="row" alignItems="flex-end" gap={days <= 30 ? 0.6 : 0.3} sx={{ height: 170, borderBottom: 1, borderColor: 'divider' }}>
              {bars.map((r) => (
                <Box key={r.date} title={`${shortDate(r.date)}: ${r.scans} scans, ${r.findings} new findings, ${r.cases} cases`} sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'flex-end', gap: '1px' }}>
                  {SERIES.map(([key, color]) => <Box key={key} sx={{ flex: 1, height: `${(r[key] / top) * 100}%`, minHeight: r[key] ? 2 : 0, bgcolor: color, borderRadius: '2px 2px 0 0' }} />)}
                </Box>
              ))}
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.8 }}>
              {bars.length > 0 && [bars[0], bars[Math.floor(bars.length / 2)], bars[bars.length - 1]].map((r, i) => <Typography key={i} variant="caption" color="text.secondary">{shortDate(r.date)}</Typography>)}
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <Box sx={{ ...panel, flex: '2 1 600px', minWidth: 0, overflow: 'hidden' }}>
              <Typography fontWeight={600} sx={{ p: 2.5 }}>How each kind of scan is doing</Typography>
              {!data.by_scan_type.length && <Typography color="text.secondary" sx={{ px: 2.5, pb: 3 }}>No scans ran in this period.</Typography>}
              {data.by_scan_type.length > 0 && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 70px 1.5fr 90px 80px', gap: 1.5, px: 2.5, py: 1.2, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, borderTop: 1, borderColor: 'divider' }}>
                  <span>Scan</span><span>Runs</span><span>Finished</span><span>Time</span><span>Findings</span>
                </Box>
              )}
              {data.by_scan_type.map((t) => (
                <Box key={t.scan_type} sx={{ display: 'grid', gridTemplateColumns: '2fr 70px 1.5fr 90px 80px', gap: 1.5, px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider', alignItems: 'center' }}>
                  <Typography fontWeight={600} noWrap>{whatLabel(t.scan_type)}</Typography>
                  <Typography sx={{ fontFamily: MONO }}>{t.runs}</Typography>
                  <Stack direction="row" alignItems="center" gap={1.2}>
                    <Box sx={{ flex: 1, height: 7, borderRadius: 1, bgcolor: alpha('#fff', 0.08) }}><Box sx={{ height: '100%', width: `${t.success_rate ?? 0}%`, borderRadius: 1, bgcolor: rateColor(t.success_rate) }} /></Box>
                    <Typography sx={{ fontFamily: MONO, width: 44, textAlign: 'right', fontSize: 13 }}>{t.success_rate == null ? '-' : `${t.success_rate}%`}</Typography>
                  </Stack>
                  <Typography color="text.secondary">{t.avg_minutes == null ? '-' : t.avg_minutes < 1 ? '<1 min' : `${t.avg_minutes} min`}</Typography>
                  <Typography sx={{ fontFamily: MONO }}>{t.findings}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ ...panel, flex: '1 1 360px', minWidth: 0, overflow: 'hidden' }}>
              <Typography fontWeight={600} sx={{ p: 2.5 }}>Why scans failed</Typography>
              {!data.failure_reasons.length && <Typography color="text.secondary" sx={{ px: 2.5, pb: 3 }}>No scan failed in this period.</Typography>}
              {data.failure_reasons.map((r) => (
                <Stack key={r.key} direction="row" gap={1.8} alignItems="flex-start" sx={{ px: 2.5, py: 1.8, borderTop: 1, borderColor: 'divider' }}>
                  <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 16, width: 30, color: '#fca5a5' }}>{r.count}</Typography>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={600}>{r.headline}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>{r.advice}</Typography>
                  </Box>
                </Stack>
              ))}
              {data.failure_reasons.length > 0 && <Button fullWidth onClick={() => navigate('/jobs')} sx={{ py: 1.4, color: GREEN, fontWeight: 700, borderTop: 1, borderColor: 'divider', borderRadius: 0 }}>See the failed scans in Scan jobs →</Button>}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
